/**
 * AssessmentPanel Component
 * Displays final simulation assessment with scores, strengths, and areas to improve
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import {
    Trophy,
    AlertCircle,
    CheckCircle,
    RefreshCw,
    Lightbulb
} from 'lucide-react';
import { MAX_HINTS } from './SimulationHint';
import { QualityTaskOutcome } from '../quality/QualityTaskOutcome';
import { ResultSummary } from '../common/ResultSummary';
import { shortDate } from '../rpat/assessmentResult';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : null);

// Names the scenario the learner just completed, its final score and status
const SimulationResultSummary = ({ simulation, scenario, score }) => {
    const itemName = simulation?.variant_title || simulation?.topic_data?.name || simulation?.job_data?.job_title || scenario?.title || 'Simulation';
    const jobTitle = simulation?.job_data?.job_title;
    return (
        <ResultSummary
            testId="sim-result-summary"
            path={['Recruiter Simulations', scenario?.title]}
            title={itemName}
            score={score}
            scoreLabel="Overall score (average of all exchanges)"
            status={score === 0 ? { label: 'Incomplete', tone: 'neutral' } : { label: 'Completed', tone: 'passed' }}
            details={[
                { label: 'Difficulty', value: capitalize(simulation?.difficulty) },
                { label: 'Job', value: jobTitle && jobTitle !== itemName ? jobTitle : null },
                { label: 'Completed', value: shortDate(simulation?.completed_at || new Date().toISOString()) },
            ]}
        />
    );
};

export const AssessmentPanel = ({
    assessment,
    scores,
    hintsUsed = 0,
    getScoreLabels,
    resetSimulation,
    qualityRefKey,
    retakeSimulation,
    simulation,
    scenario
}) => {
    if (!assessment) return null;

    return (
        <Card className="max-w-3xl mx-auto">
            <CardHeader className="text-center pb-2">
                <div className="flex justify-center mb-4">
                    <div className={`p-4 rounded-full ${scores?.overall === 0 ? 'bg-muted' : 'bg-primary/10'}`}>
                        {scores?.overall === 0 ? (
                            <AlertCircle className="h-12 w-12 text-muted-foreground" />
                        ) : (
                            <Trophy className="h-12 w-12 text-primary" />
                        )}
                    </div>
                </div>
                <CardTitle className="text-2xl">
                    {scores?.overall === 0 ? 'Simulation Incomplete' : 'Simulation Complete!'}
                </CardTitle>
                <CardDescription>
                    {scores?.overall === 0 
                        ? 'Complete at least 3 exchanges to receive a score'
                        : "Here's your detailed performance assessment"}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* What was completed, final score and status */}
                <SimulationResultSummary simulation={simulation} scenario={scenario} score={scores?.overall || 0} />

                <div className="flex justify-center">
                    <div
                        className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs text-amber-700 dark:text-amber-400"
                        data-testid="assessment-hints-used"
                    >
                        <Lightbulb className="h-3.5 w-3.5" />
                        Hints used: {hintsUsed} of {MAX_HINTS}
                        <span className="text-muted-foreground">· no score penalty</span>
                    </div>
                </div>

                {/* Score Breakdown - Dynamic based on simulation type */}
                <div className="grid gap-3 md:grid-cols-4">
                    {getScoreLabels().map((item, i) => (
                        <div key={i} className="text-center p-3 rounded-lg bg-muted/50">
                            <div className={`text-2xl font-bold ${scores?.[item.key] === 0 ? 'text-muted-foreground' : ''}`}>
                                {scores?.[item.key] || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">{item.label}</div>
                        </div>
                    ))}
                </div>

                {/* Strengths & Improvements */}
                <div className="grid gap-4 md:grid-cols-2">
                    <div className={`p-4 rounded-xl ${assessment.strengths?.length > 0 ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-muted/50 border border-muted'}`}>
                        <h4 className={`font-semibold flex items-center gap-2 mb-3 ${assessment.strengths?.length > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                            <CheckCircle className="h-5 w-5" />
                            Strengths
                        </h4>
                        <ul className="space-y-2">
                            {assessment.strengths?.length > 0 ? assessment.strengths.map((s, i) => (
                                <li key={i} className="text-sm flex items-start gap-2">
                                    <span className="text-emerald-500 mt-0.5">✓</span>
                                    {s}
                                </li>
                            )) : (
                                <li className="text-sm text-muted-foreground">Complete more exchanges to see strengths</li>
                            )}
                        </ul>
                    </div>
                    <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                        <h4 className="font-semibold text-yellow-600 dark:text-yellow-400 flex items-center gap-2 mb-3">
                            <AlertCircle className="h-5 w-5" />
                            Areas to Improve
                        </h4>
                        <ul className="space-y-2">
                            {assessment.improvements?.map((s, i) => (
                                <li key={i} className="text-sm flex items-start gap-2">
                                    <span className="text-yellow-500 mt-0.5">→</span>
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {assessment.overall_feedback && (
                    <div className="p-4 rounded-xl bg-muted border">
                        <h4 className="font-medium mb-2">Overall Feedback</h4>
                        <p className="text-sm text-muted-foreground">{assessment.overall_feedback}</p>
                    </div>
                )}

                <QualityTaskOutcome kind="recruiter" refKey={qualityRefKey} attemptScore={scores?.overall || 0} onRetake={retakeSimulation}>
                    <div className="flex gap-3 pt-4">
                        <Button onClick={resetSimulation} className="flex-1 btn-primary" data-testid="new-simulation">
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Try Another Simulation
                        </Button>
                    </div>
                </QualityTaskOutcome>
            </CardContent>
        </Card>
    );
};
